import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Loader2, Zap, Trash2, ArrowRight, Play } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { AUTO_TRIGGERS, AUTO_ACTIONS } from "@/lib/constants";
import IntegrationsCatalog from "./IntegrationsCatalog";

const TID = {
  add: "automation-add",
  name: "automation-name",
  trigger: "automation-trigger",
  action: "automation-action",
  submit: "automation-submit",
  trigger_btn: (id) => `automation-fire-${id}`,
};

export default function TenantAutomations() {
  const [items, setItems] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", trigger: "appointment.completed", conditions: {},
    action: "send_message", action_params: { channel: "whatsapp", message: "Olá, obrigado!" }, is_active: true,
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, ev] = await Promise.all([
        api.get("/tenant/automations"),
        api.get("/tenant/automations/events", { params: { limit: 20 } }),
      ]);
      setItems(r.data.items || []);
      setEvents(ev.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/tenant/automations", form);
      toast.success("Automação criada");
      setOpen(false);
      setForm({
        name: "", description: "", trigger: "appointment.completed", conditions: {},
        action: "send_message", action_params: { channel: "whatsapp", message: "" }, is_active: true,
      });
      load();
    } catch (er) { toast.error(formatApiError(er)); }
    finally { setBusy(false); }
  };

  const fire = async (id) => {
    try { await api.post(`/tenant/automations/${id}/trigger`, {}); toast.success("Automação disparada (teste)"); setTimeout(load, 600); }
    catch (er) { toast.error(formatApiError(er)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir automação?")) return;
    await api.delete(`/tenant/automations/${id}`); load();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl space-y-6">
      <IntegrationsCatalog />

      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
            <Zap className="w-3.5 h-3.5" /> Automações
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Quando isso acontecer → Faça aquilo</h1>
          <p className="text-sm text-muted-foreground mt-1">Configure gatilhos e ações automáticas para crescer no piloto automático.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={TID.add} className="gap-2" style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              <Plus className="w-4 h-4" /> Nova automação
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Nova automação</DialogTitle>
              <DialogDescription>Se o gatilho acontecer, então execute a ação.</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5"><Label>Nome</Label><Input required data-testid={TID.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Descrição</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="space-y-1.5">
                <Label>Se (gatilho)</Label>
                <Select value={form.trigger} onValueChange={(v) => setForm({ ...form, trigger: v })}>
                  <SelectTrigger data-testid={TID.trigger}><SelectValue /></SelectTrigger>
                  <SelectContent>{AUTO_TRIGGERS.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Então (ação)</Label>
                <Select value={form.action} onValueChange={(v) => setForm({ ...form, action: v, action_params: defaultParams(v) })}>
                  <SelectTrigger data-testid={TID.action}><SelectValue /></SelectTrigger>
                  <SelectContent>{AUTO_ACTIONS.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <ActionParams action={form.action} params={form.action_params} onChange={(p) => setForm({ ...form, action_params: p })} />
              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid={TID.submit} className="gap-2" style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />} Criar automação
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Banner explicativo passo-a-passo */}
      <Card className="p-5 rounded-tenant border-tenant/30 bg-tenant/5" data-testid="automations-howto">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-tenant shrink-0 mt-0.5" />
          <div className="flex-1 space-y-3">
            <div>
              <div className="font-semibold mb-1">Como funcionam as automações — passo a passo</div>
              <p className="text-sm text-muted-foreground">
                Uma automação é uma regra <strong>Se → Então</strong>. Você escolhe o evento (gatilho) e o que deve acontecer (ação). O sistema executa sozinho, sem você precisar lembrar.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-3 text-xs">
              <div className="bg-background border border-border rounded-lg p-3">
                <div className="font-bold text-tenant mb-1">1. Escolha o GATILHO</div>
                <div className="text-muted-foreground">
                  O que precisa acontecer? Ex.: aniversário do cliente, cliente 30 dias sem voltar, pontos atingidos, atendimento concluído, NPS detrator.
                </div>
              </div>
              <div className="bg-background border border-border rounded-lg p-3">
                <div className="font-bold text-tenant mb-1">2. Escolha a AÇÃO</div>
                <div className="text-muted-foreground">
                  O que o sistema deve fazer? Ex.: enviar mensagem (WhatsApp/Email), adicionar tag, dar pontos bônus, criar tarefa.
                </div>
              </div>
              <div className="bg-background border border-border rounded-lg p-3">
                <div className="font-bold text-tenant mb-1">3. Salve e ative</div>
                <div className="text-muted-foreground">
                  A automação roda diariamente em background. Acompanhe quantas vezes disparou na linha do tempo abaixo.
                </div>
              </div>
            </div>

            <details className="text-xs">
              <summary className="cursor-pointer text-tenant font-medium">Ver exemplos prontos →</summary>
              <ul className="mt-2 ml-4 list-disc space-y-1 text-muted-foreground">
                <li><strong>Se</strong> cliente faz aniversário, <strong>então</strong> envia &quot;Parabéns! Aqui está 10% off no próximo atendimento 🎁&quot;</li>
                <li><strong>Se</strong> cliente 30 dias sem voltar, <strong>então</strong> envia &quot;Sentimos sua falta! Que tal agendar?&quot; + cupom</li>
                <li><strong>Se</strong> atendimento concluído, <strong>então</strong> envia link da pesquisa NPS (usa variável <code>{"{{nps_link}}"}</code>)</li>
                <li><strong>Se</strong> cliente atingiu 500 pontos, <strong>então</strong> envia &quot;Você liberou um voucher de R$ 50!&quot;</li>
                <li><strong>Se</strong> indicação validada, <strong>então</strong> dá 100 pontos bônus para o indicador</li>
              </ul>
            </details>
          </div>
        </div>
      </Card>

      {loading ? <div className="p-10 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div> :
        items.length === 0 ? (
          <Card className="p-12 text-center text-muted-foreground rounded-tenant">
            Nenhuma automação configurada. Ex: &quot;Se atendimento concluído, então enviar mensagem de agradecimento&quot;.
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-3 mb-8">
            {items.map((r) => (
              <Card key={r.id} className="p-5 rounded-tenant border-border">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold text-lg">{r.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap text-xs">
                      <Badge variant="outline">Se {labelOf(AUTO_TRIGGERS, r.trigger)}</Badge>
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <Badge style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
                        {labelOf(AUTO_ACTIONS, r.action)}
                      </Badge>
                    </div>
                    {r.fired_count > 0 && <div className="text-xs text-muted-foreground mt-3">Disparada {r.fired_count}x · última {r.last_fired_at ? new Date(r.last_fired_at).toLocaleString("pt-BR") : "—"}</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button size="sm" variant="ghost" data-testid={TID.trigger_btn(r.id)} onClick={() => fire(r.id)}><Play className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => remove(r.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Eventos disparados recentemente</div>
        <Card className="rounded-tenant border-border overflow-hidden">
          {events.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Sem eventos ainda — dispare uma automação acima.</div> :
            <ul className="divide-y divide-border text-sm">
              {events.map((ev, i) => (
                <li key={i} className="px-5 py-2.5 flex items-center justify-between">
                  <div>
                    <Badge variant="outline">{ev.type}</Badge>
                    <span className="ml-2 text-xs text-muted-foreground">{ev.message || ev.title || ev.template || ""}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{new Date(ev.created_at).toLocaleString("pt-BR")}</span>
                </li>
              ))}
            </ul>}
        </Card>
      </div>
    </div>
  );
}

function defaultParams(action) {
  switch (action) {
    case "send_message": return { channel: "whatsapp", message: "" };
    case "award_points": return { points: 5 };
    case "tag_client": return { tag: "VIP" };
    case "create_followup": return { title: "Follow-up", due_in_days: 3 };
    default: return {};
  }
}

function labelOf(list, v) { return (list.find((x) => x.v === v) || {}).l || v; }

function ActionParams({ action, params, onChange }) {
  const [templates, setTemplates] = useState([]);
  useEffect(() => {
    if (action === "send_message") {
      api.get("/tenant/message-templates").then(({ data }) => setTemplates(data.items || [])).catch(() => {});
    }
  }, [action]);

  if (action === "send_message") {
    return (
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label>Template (opcional — usa variáveis)</Label>
          <Select value={params.template_id || "__none"} onValueChange={(v) => onChange({ ...params, template_id: v === "__none" ? null : v })}>
            <SelectTrigger><SelectValue placeholder="Nenhum (usar mensagem livre)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none">Mensagem livre (sem template)</SelectItem>
              {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.channel}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label>Canal</Label>
            <Select value={params.channel || "whatsapp"} onValueChange={(v) => onChange({ ...params, channel: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5 col-span-2"><Label>Mensagem (se sem template)</Label><Input value={params.message || ""} onChange={(e) => onChange({ ...params, message: e.target.value })} /></div>
        </div>
      </div>
    );
  }
  if (action === "award_points") {
    return <div className="space-y-1.5"><Label>Pontos</Label><Input type="number" value={params.points || 0} onChange={(e) => onChange({ ...params, points: Number(e.target.value) })} /></div>;
  }
  if (action === "tag_client") {
    return <div className="space-y-1.5"><Label>Tag</Label><Input value={params.tag || ""} onChange={(e) => onChange({ ...params, tag: e.target.value })} /></div>;
  }
  if (action === "create_followup") {
    return (
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>Título</Label><Input value={params.title || ""} onChange={(e) => onChange({ ...params, title: e.target.value })} /></div>
        <div className="space-y-1.5"><Label>Em quantos dias</Label><Input type="number" value={params.due_in_days || 0} onChange={(e) => onChange({ ...params, due_in_days: Number(e.target.value) })} /></div>
      </div>
    );
  }
  return null;
}
